import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import apiService, { getSuppliers } from "../../services/apiService";
import "../POS/ReceivingPO.css";

export default function ReceivingPO() {
  const [poList, setPoList] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [receivingItems, setReceivingItems] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);

  const listInFlightRef = useRef(false);
  const detailsInFlightRef = useRef(false);
  const detailsReqIdRef = useRef(0);
  const selectedPORef = useRef(null);
  const receivingItemsRef = useRef([]);

  useEffect(() => {
    selectedPORef.current = selectedPO;
  }, [selectedPO]);

  useEffect(() => {
    receivingItemsRef.current = receivingItems;
  }, [receivingItems]);

  const mapSupplierNames = useCallback((pos, supplierList) => {
    return (pos || []).map((po) => {
      const supplier = (supplierList || []).find(
        (s) => s.SupplierID === po.SupplierID
      );
      return supplier ? { ...po, SupplierName: supplier.SupplierName } : po;
    });
  }, []);

  const safeDate = useCallback((d) => {
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? "-" : dt.toLocaleDateString();
  }, []);

  const loadPOList = useCallback(async () => {
    if (listInFlightRef.current) return;
    listInFlightRef.current = true;
    setLoadingList(true);

    try {
      const [allPOs, supplierList] = await Promise.all([
        apiService.getPurchaseOrders(),
        getSuppliers(),
      ]);

      setPoList(mapSupplierNames(allPOs, supplierList));
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error loading POs:", error);
    } finally {
      setLoadingList(false);
      listInFlightRef.current = false;
    }
  }, [mapSupplierNames]);

  const loadPODetails = useCallback(async (poid, keepReceiveNow = true) => {
    if (!poid) return;
    if (detailsInFlightRef.current) return;

    detailsInFlightRef.current = true;
    setLoadingDetails(true);

    const reqId = ++detailsReqIdRef.current;

    try {
      const po = await apiService.getPurchaseOrder(poid);

      if (reqId !== detailsReqIdRef.current) return;
      if (
        selectedPORef.current?.POID &&
        selectedPORef.current.POID !== poid
      ) {
        return;
      }

      const prevMap = new Map(
        (receivingItemsRef.current || []).map((x) => [x.PODetailID, x.ReceiveNow])
      );

      const items = (po.items || []).map((item) => ({
        ...item,
        ReceiveNow: keepReceiveNow ? prevMap.get(item.PODetailID) ?? 0 : 0,
      }));

      setSelectedPO(po);
      setReceivingItems(items);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching PO:", error);
    } finally {
      setLoadingDetails(false);
      detailsInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadPOList();
  }, [loadPOList]);

  useEffect(() => {
    let alive = true;
    let timerId = null;

    const poll = async () => {
      if (!alive) return;

      if (!selectedPORef.current && !document.hidden) {
        await loadPOList();
      }

      timerId = setTimeout(poll, 10000);
    };

    poll();

    return () => {
      alive = false;
      if (timerId) clearTimeout(timerId);
    };
  }, [loadPOList]);

  useEffect(() => {
    let alive = true;
    let timerId = null;

    const pollDetails = async () => {
      if (!alive) return;

      const currentPOID = selectedPORef.current?.POID;
      if (currentPOID && !document.hidden) {
        await loadPODetails(currentPOID, true);
      }

      timerId = setTimeout(pollDetails, 10000);
    };

    if (selectedPO?.POID) {
      pollDetails();
    }

    return () => {
      alive = false;
      if (timerId) clearTimeout(timerId);
    };
  }, [selectedPO?.POID, loadPODetails]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((poList?.length || 0) / PAGE_SIZE));
  }, [poList]);

  const pagedPOs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return poList.slice(start, start + PAGE_SIZE);
  }, [poList, page]);

  useEffect(() => {
    setPage((p) => {
      const tp = Math.max(1, Math.ceil((poList?.length || 0) / PAGE_SIZE));
      return Math.min(p, tp);
    });
  }, [poList]);

  const handleSelectPO = async (poid) => {
    await loadPODetails(poid, false);
  };

  const handleQtyChange = (index, value) => {
    const updated = [...receivingItems];
    const inputQty = parseInt(value, 10) || 0;
    const pending = Number(updated[index].PendingQty) || 0;

    if (inputQty > pending) {
      alert("Received quantity cannot exceed pending quantity!");
      return;
    }

    updated[index].ReceiveNow = inputQty;
    setReceivingItems(updated);
  };

  const handleSaveReceiving = async () => {
    try {
      const updatedItems = receivingItems.map((item) => {
        const receiveNow = Number(item.ReceiveNow) || 0;
        const receivedQty = Number(item.ReceivedQty) || 0;
        const orderedQty = Number(item.OrderedQty) || 0;

        const newReceived = receivedQty + receiveNow;
        const newPending = Math.max(orderedQty - newReceived, 0);
        const newStatus = newPending === 0 ? "Completed" : "Partially Received";

        return {
          ...item,
          ReceivedQty: newReceived,
          PendingQty: newPending,
          Status: newStatus,
        };
      });

      const overallStatus = updatedItems.every((i) => i.Status === "Completed")
        ? "Completed"
        : "Partially Received";

      const payload = {
        Status: overallStatus,
        items: updatedItems,
      };

      await apiService.receivePurchaseOrder(selectedPO.POID, payload);

      alert("Receiving updated successfully!");

      detailsReqIdRef.current++;
      setSelectedPO(null);
      setReceivingItems([]);

      await loadPOList();
    } catch (error) {
      console.error("Error saving receiving:", error);
      alert("❌ Failed to save receiving.");
    }
  };

  return (
    <div className="receiving-container">
      <h1>Receive Purchase Orders</h1>

      <div style={{ marginBottom: 10, fontSize: 12, opacity: 0.8 }}>
        <span>
          Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : "—"}
        </span>
        {(loadingList || loadingDetails) && (
          <span style={{ marginLeft: 10 }}>⏳ Updating…</span>
        )}
      </div>

      {!selectedPO ? (
        <>
          <h3>All Purchase Orders</h3>

          <table className="po-table">
            <thead>
              <tr>
                <th>PO Number</th>
                <th>Supplier</th>
                <th>Order Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {poList.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center" }}>
                    {loadingList ? "Loading…" : "No Purchase Orders found"}
                  </td>
                </tr>
              ) : (
                pagedPOs.map((po) => (
                  <tr key={po.POID}>
                    <td>{po.PONumber}</td>
                    <td>{po.SupplierName || po.Supplier?.SupplierName || "-"}</td>
                    <td>{safeDate(po.OrderDate)}</td>
                    <td>{po.Status}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => handleSelectPO(po.POID)}
                      >
                        {po.Status === "Completed" ? "View" : "Receive"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {poList.length > 0 && (
            <div className="pagination">
              <button
                type="button"
                className="btn-page"
                onClick={() => setPage(1)}
                disabled={page === 1}
              >
                First
              </button>

              <button
                type="button"
                className="btn-page"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Prev
              </button>

              <span className="page-info">
                Page <b>{page}</b> of <b>{totalPages}</b>
              </span>

              <button
                type="button"
                className="btn-page"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>

              <button
                type="button"
                className="btn-page"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
              >
                Last
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <h2>
            {selectedPO.Status === "Completed"
              ? `View Completed PO #${selectedPO.PONumber}`
              : `Receiving for PO #${selectedPO.PONumber}`}
          </h2>

          <table className="po-table">
            <thead>
              <tr>
                <th>Catalog No</th>
                <th>Description</th>
                <th>Ordered</th>
                <th>Received</th>
                <th>Pending</th>
                <th>Receive Now</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {receivingItems.map((item, idx) => (
                <tr key={item.PODetailID}>
                  <td>{item.CatalogNo}</td>
                  <td>{item.ItemName}</td>
                  <td>{item.OrderedQty}</td>
                  <td>{item.ReceivedQty}</td>
                  <td>{item.PendingQty}</td>
                  <td>
                    {selectedPO.Status === "Completed" ? (
                      <span>-</span>
                    ) : (
                      <input
                        type="number"
                        value={item.ReceiveNow}
                        min="0"
                        max={item.PendingQty}
                        onChange={(e) => handleQtyChange(idx, e.target.value)}
                        style={{ width: "70px" }}
                      />
                    )}
                  </td>
                  <td>{item.Status}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: "15px" }}>
            {selectedPO.Status !== "Completed" && (
              <button
                type="button"
                onClick={handleSaveReceiving}
                className="btn-save"
              >
                Save Receiving
              </button>
            )}

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                detailsReqIdRef.current++;
                setSelectedPO(null);
                setReceivingItems([]);
              }}
              className="btn-back"
            >
              Back
            </button>
          </div>
        </>
      )}
    </div>
  );
}